import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthShell, AuthFooter, UnlockOverlay, EyeToggle } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REDIRECT_DELAY_MS = 1600; // ms before redirect after successful auth

function CityLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestEmail, setRequestEmail] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const navigate = useNavigate();

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setRequestLoading(true);
    try {
      await axios.post(`${API}/auth/request-city-access`, { email: requestEmail });
      setRequestSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setRequestLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/admin-login`, { email, password });
      const { token, email: userEmail, role } = response.data;
      if (role !== "city_official") {
        toast.error("This portal is for City Officials only.");
        setLoading(false);
        return;
      }
      localStorage.setItem("token", token);
      localStorage.setItem("email", userEmail);
      localStorage.setItem("role", role);
      setUnlocking(true);
      setTimeout(() => navigate("/city"), REDIRECT_DELAY_MS);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {unlocking && <UnlockOverlay label="Accessing City Portal" />}

      <AuthShell
        eyebrow="City of Long Beach"
        title="Official Portal"
        subtitle="Transparency & compliance access"
        after={
          <p className="text-center mt-6">
            <button onClick={() => navigate("/")}
              className="text-xs text-mute hover:text-ink transition-colors">
              Back to applicant login
            </button>
          </p>
        }
        footer={<AuthFooter>Equal &copy; 2025 · Tkach Ventures</AuthFooter>}
      >
        <form onSubmit={handleSubmit} className="space-y-3">
          <input type="email" placeholder="City email address" value={email}
            onChange={e => setEmail(e.target.value)} required
            autoComplete="email" className="input-flat h-12"
            data-testid="city-email-input" />

          <div className="relative">
            <input type={showPassword ? "text" : "password"} placeholder="Password"
              value={password} onChange={e => setPassword(e.target.value)} required
              autoComplete="current-password"
              className="input-flat h-12 pr-11"
              data-testid="city-password-input" />
            <EyeToggle shown={showPassword} onClick={() => setShowPassword(!showPassword)} />
          </div>

          <button type="submit" disabled={loading}
            className="btn-primary w-full h-11"
            data-testid="city-login-button">
            {loading ? "Authenticating..." : "Access City Portal"}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-line" />
          <span className="text-mute text-xs font-mono">or</span>
          <div className="flex-1 h-px bg-line" />
        </div>

        {/* First-time setup */}
        <div className="rounded-[10px] p-4 space-y-3 bg-mint border border-[#cfe9db]">
          {!showRequestForm ? (
            <>
              <p className="text-mute text-xs text-center">New to Equal? Request access below.</p>
              <button type="button"
                onClick={() => navigate("/forgot-password?setup=true")}
                className="btn-ghost w-full h-9 text-equal-dark border-equal/40 hover:border-equal hover:bg-white"
                data-testid="city-first-time-button">
                Already have an account? Create your login
              </button>
              <button type="button"
                onClick={() => setShowRequestForm(true)}
                className="btn-ghost w-full h-9 hover:bg-white">
                Request City Official Access
              </button>
            </>
          ) : requestSent ? (
            <div className="text-center space-y-2 py-2">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto bg-equal">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <p className="text-ink text-sm font-medium">Setup link sent!</p>
              <p className="text-mute text-xs leading-relaxed">Check your inbox for instructions to create your login.</p>
              <button type="button" onClick={() => { setShowRequestForm(false); setRequestSent(false); setRequestEmail(""); }}
                className="text-xs text-equal-dark hover:text-ink transition-colors">
                Back to login
              </button>
            </div>
          ) : (
            <form onSubmit={handleRequestAccess} className="space-y-2">
              <p className="text-mute text-xs text-center">Enter your city email to receive access</p>
              <input type="email" placeholder="your.name@longbeach.gov"
                value={requestEmail} onChange={e => setRequestEmail(e.target.value)} required
                className="input-flat h-9"
                data-testid="request-access-email" />
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowRequestForm(false)}
                  className="btn-quiet flex-1 h-9">
                  Cancel
                </button>
                <button type="submit" disabled={requestLoading}
                  className="btn-primary flex-1 h-9 text-xs"
                  data-testid="request-access-submit">
                  {requestLoading ? "Sending..." : "Send Setup Link"}
                </button>
              </div>
            </form>
          )}
        </div>
      </AuthShell>
    </>
  );
}

export default CityLoginPage;
